import { useSyncExternalStore } from 'react';

/**
 * Bilingual support for the marketing site.
 *
 * Implementation note: this is intentionally lighter than the portal's
 * Zustand-backed store (`apps/web/src/stores/ui-store.ts`). The marketing
 * site has no auth, no theme picker, no numerals toggle — just a single
 * language preference. We back it with a tiny `useSyncExternalStore`
 * subscription so every component re-renders on toggle without React
 * Context overhead.
 *
 * The selected language is mirrored onto `<html lang>` and `<html dir>`
 * so CSS variants (`[lang='fa']`, `[dir='rtl']`) and logical properties
 * (`ps-`, `pe-`, `ms-`, `me-`, `start-*`, `end-*`) flip automatically.
 */

export type Lang = 'en' | 'fa';

const STORAGE_KEY = 'jam-marketing-lang';
const listeners = new Set<() => void>();
let currentLang: Lang = readInitialLang();

function readInitialLang(): Lang {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'fa') return stored;
  } catch {
    /* localStorage may be blocked — fall through */
  }
  return 'en';
}

export function applyLangToDocument(lang: Lang): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  html.setAttribute('lang', lang);
  html.setAttribute('dir', lang === 'fa' ? 'rtl' : 'ltr');
}

export function setLang(next: Lang): void {
  if (next === currentLang) return;
  currentLang = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore */
  }
  applyLangToDocument(next);
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): Lang {
  return currentLang;
}

function getServerSnapshot(): Lang {
  return 'en';
}

export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Translate helper. Each component owns its own `STRINGS` table and
 * passes it to `t(strings, key, lang)` so we avoid one giant central
 * dictionary. Mirrors the portal's `apps/web/src/lib/i18n.ts` shape.
 */
export type StringTable<K extends string> = Record<K, { en: string; fa: string }>;

export function t<K extends string>(
  strings: StringTable<K>,
  key: K,
  lang: Lang,
): string {
  return strings[key][lang];
}

/** Initialize the document on first import (client only). */
if (typeof document !== 'undefined') {
  applyLangToDocument(currentLang);
}
